import { getBranchQueryArgs, getCurrentBranchName } from 'app/branches/selectors';
import interpolate from 'app/common/utils/interpolate';
import { handleRoutine } from 'app/common/utils/saga';
import { getVersion } from 'app/schema/selectors';
import type { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { fetchSchemaRoutine } from '../branches/sagas/fetch-schema';
import { showConfirm, waitForConfirm } from '../confirm';
import { mutateAdmin as mutate } from '../graphql-client';
import { showMessage } from '../messages/actions';
import { getProjectEntitlement } from '../project/selectors';
import { getValues, onFormSuccess } from '../schema-form';
import { addLocale, removeLocale, setDefaultLocale } from './actions';
import { ADD_LOCALE_MUTATION, REMOVE_LOCALE_MUTATION, SET_DEFAULT_LOCALE_MUTATION } from './api';
import {
  FORM_NAME,
  REMOVE_CONFIRM_MESSAGE,
  REMOVE_CONFIRM_OK,
  REMOVE_CONFIRM_TITLE,
  SET_DEFAULT_CONFIRM_MESSAGE,
  SET_DEFAULT_CONFIRM_OK,
  SET_DEFAULT_CONFIRM_TITLE,
  SET_SINGLE_LOCALE_CONFIRM_MESSAGE
} from './constants';
import { getDefaultLocale } from './selectors';
import type { LocaleMutationError, LocaleMutationPayload } from './types';
import { getLocaleCode } from './util/locale-code';

export function getMutationSaga(fieldName: string, mutation: ReturnType<typeof mutate>) {
  return function* (payload: LocaleMutationPayload): SagaIterator {
    const version = yield select(getVersion);
    const branch = yield select(getCurrentBranchName);
    const branchArgs = yield select(getBranchQueryArgs, {});
    yield call(mutation, {
      input: { [fieldName]: payload.locale, version, ...branchArgs }
    });
    yield put(fetchSchemaRoutine.requested(undefined, { runtime: true, branch }));
    yield put(fetchSchemaRoutine.requested(undefined, { runtime: false, branch }));
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function withConfirmation(mutationSaga: ReturnType<typeof getMutationSaga>, confirmParams: any) {
  return function* (payload: LocaleMutationPayload): SagaIterator {
    payload.defaultLocale = yield select(getDefaultLocale);
    yield put(
      showConfirm({
        confirmText: payload.locale,
        ...confirmParams,
        title: interpolate(confirmParams.title, isRecord(payload) ? payload : {}),
        message: interpolate(confirmParams.message, isRecord(payload) ? payload : {})
      })
    );

    if (yield call(waitForConfirm)) {
      yield call(mutationSaga, payload);
    }
  };
}

export const setDefaultMutatationSaga = getMutationSaga('defaultLocale', mutate(SET_DEFAULT_LOCALE_MUTATION));

export function* setDefaultSaga(payload: LocaleMutationPayload): SagaIterator {
  const numLocales = yield select(getProjectEntitlement, {
    entitlement: 'locales'
  });
  const mutipleLocales = numLocales > 1;
  const confirmationSaga = yield call(withConfirmation, setDefaultMutatationSaga, {
    type: 'delete',
    title: SET_DEFAULT_CONFIRM_TITLE,
    message: mutipleLocales ? SET_DEFAULT_CONFIRM_MESSAGE : SET_SINGLE_LOCALE_CONFIRM_MESSAGE,
    okLabel: mutipleLocales ? SET_DEFAULT_CONFIRM_OK : undefined,
    confirmText: mutipleLocales ? payload.locale : undefined
  });
  yield call(confirmationSaga, payload);
}

const removeSaga = withConfirmation(getMutationSaga('locale', mutate(REMOVE_LOCALE_MUTATION)), {
  type: 'delete',
  title: REMOVE_CONFIRM_TITLE,
  message: REMOVE_CONFIRM_MESSAGE,
  okLabel: REMOVE_CONFIRM_OK
});

export function* formSubmitSaga(): SagaIterator {
  const version = yield select(getVersion);
  const values = yield select(getValues, { formName: FORM_NAME });
  const locale = getLocaleCode(values);
  if (values.default) {
    yield put(setDefaultLocale({ locale, version }));
  } else {
    yield put(addLocale({ locale, version }));
  }
}

export function* handleLocaleError({ payload }: LocaleMutationError): SagaIterator {
  const message = payload.rawError?.[0]?.message ?? 'Something went wrong. Refresh and try again.';
  yield put(showMessage('error', message, 5000));
}

export default function* root() {
  yield all([
    takeLatest(onFormSuccess(FORM_NAME), formSubmitSaga),
    handleRoutine(setDefaultLocale, setDefaultSaga),
    handleRoutine(removeLocale, removeSaga),
    handleRoutine(addLocale, getMutationSaga('locale', mutate(ADD_LOCALE_MUTATION))),
    takeLatest(addLocale.rejected, handleLocaleError),
    takeLatest(removeLocale.rejected, handleLocaleError),
    takeLatest(setDefaultLocale.rejected, handleLocaleError)
  ]);
}
